import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useProfileRoutesConditions } from '@pages/Profile/ProfileRoutes'
import { useSubscriptionRoutesConditions } from '@pages/Subscriptions/SubscriptionsRoutes'
import { useToolsRoutesConditions } from '@pages/Tools/ToolsRoutes'
import { useTraderRoomRoutesConditions } from '@pages/Traders-Room/TradersRoom.routes'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { ChartPieIcon } from '@ui/icons/ChartPie.icon'
import { CoinsIcon } from '@ui/icons/Coins.icon'
import { GraphIcon } from '@ui/icons/Graph.icon'
import { LayoutIcon } from '@ui/icons/Layout.icon'

import { isDepositBeforeKYC } from '@utils/AccountAccess/accountKYCStatuses'
import { CommonPaths } from '@utils/path-common'

import { TickmillProductType } from '@model/TickmillProductType'

import { useFundingNav } from '@hooks/useFundingNav'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { MenuSection, filterMenuSections } from './useMenuSections'

export const useActionSections = (): {
  actionSections: MenuSection[]
} => {
  const { t } = useTranslation()
  const { product } = useProductReadContext()
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()

  const { account } = useAccountReadContext()

  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const { isFirstTimeDepositVisible } = useProfileRoutesConditions()
  const { isToolsVisible } = useToolsRoutesConditions()
  const { isSubscriptionsVisible } = useSubscriptionRoutesConditions()

  const showDepositButton = isTradersRoomVisible || isFirstTimeDepositVisible
  const showCreateTradingAccountButton = isTradersRoomVisible || isDepositBeforeKYC(account)
  const showPlatformButton = isToolsVisible || isDepositBeforeKYC(account)

  const tracker = useDepositTracker()

  const actionSections: MenuSection[] = useMemo(
    () => [
      {
        id: 'deposit_action',
        label: t('Deposit'),
        icon: CoinsIcon,
        path: CommonPaths.Deposit,
        condition: showDepositButton,
        onClick: () => {
          tracker.start({ entry_point: 'menu_wallets' })
          navigateToFunding(CommonPaths.Deposit)
        },
      },
      {
        id: 'market_data_action',
        label: t('Market Data.title'),
        icon: ChartPieIcon,
        path: '/dashboard/subscriptions/market-data',
        products: [TickmillProductType.ETD],
        condition: isSubscriptionsVisible,
      },
      {
        id: 'create_ta_action',
        label: t('Create Trading Account'),
        icon: GraphIcon,
        path: CommonPaths.NewLiveTradingAccount,
        products: [TickmillProductType.CFD],
        condition: showCreateTradingAccountButton,
      },
      {
        id: 'create_ta_cfd_action',
        label: t('Tabs.Trading Platforms'),
        icon: LayoutIcon,
        path: '/dashboard/traders-room/platforms',
        onClick: () => {
          // Force navigation to base path to trigger platform selection
          // Add timestamp to force re-navigation even on same route
          const currentPath = window.location.pathname
          const basePath = '/dashboard/traders-room/platforms'

          if (currentPath.includes(basePath)) {
            // If already on platforms page, navigate away briefly then back
            navigate('/dashboard/traders-room', { replace: true })
            setTimeout(() => {
              navigate(basePath, { replace: true })
            }, 0)
          } else {
            navigate(basePath, { replace: true })
          }
        },
        products: [TickmillProductType.CFD],
        condition: showPlatformButton,
      },
      {
        id: 'create_ta_etd_action',
        label: t('Tabs.Trading Platforms'),
        icon: LayoutIcon,
        path: '/dashboard/subscriptions/trading-platforms',
        products: [TickmillProductType.ETD],
        condition: showPlatformButton,
      },
    ],
    [
      t,
      showDepositButton,
      isSubscriptionsVisible,
      showCreateTradingAccountButton,
      showPlatformButton,
      tracker,
      navigate,
      navigateToFunding,
    ]
  )

  const filteredSections = useCallback(filterMenuSections, [])

  const filteredMenuSections = useMemo(
    () => filteredSections(actionSections, product),
    [filteredSections, actionSections, product]
  )

  return { actionSections: filteredMenuSections }
}
